/**
 * KdpCompliancePanel.jsx
 *
 * KDP (Kindle Direct Publishing) Compliance Checker.
 * Runs a full compliance scan across:
 *   • Metadata (title, subtitle, author, BISAC, keywords)
 *   • Content Policy (prohibited content, adult flags, public domain)
 *   • Front/Back Matter (copyright, TOC, About the Author)
 *   • Formatting (chapter heading consistency, spacing, scene breaks)
 *
 * Opens as a Dialog from Tools menu.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  AlertCircle,
  AlertTriangle,
  BookMarked,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Download,
  FileText,
  Info,
  Loader2,
  Play,
  RefreshCw,
  ShieldCheck,
  Tag,
  Type,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 2500;

const AREA_META = {
  metadata:         { label: 'Metadata',          Icon: Tag,          color: 'text-blue-500'  },
  content_policy:   { label: 'Content Policy',    Icon: ShieldCheck,  color: 'text-purple-500' },
  front_back_matter:{ label: 'Front / Back Matter',Icon: BookMarked,  color: 'text-amber-500'  },
  formatting:       { label: 'Formatting',         Icon: Type,         color: 'text-emerald-500'},
};

const SEVERITY_CFG = {
  error:   { label: 'Error',   Icon: AlertCircle,   cls: 'text-rose-600 bg-rose-50 border-rose-200 dark:bg-rose-950/40 dark:border-rose-800' },
  warning: { label: 'Warning', Icon: AlertTriangle, cls: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:border-amber-800' },
  info:    { label: 'Info',    Icon: Info,          cls: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:border-blue-800' },
};

function ScoreBadge({ score, status }) {
  if (score == null) return null;
  const colorMap = {
    ready:        'bg-emerald-500 text-white',
    almost_ready: 'bg-amber-500 text-white',
    needs_work:   'bg-orange-500 text-white',
    not_ready:    'bg-rose-500 text-white',
  };
  const labelMap = {
    ready:        'KDP Ready',
    almost_ready: 'Almost Ready',
    needs_work:   'Needs Work',
    not_ready:    'Not Ready',
  };
  return (
    <div className="flex items-center gap-2">
      <span className={cn('text-2xl font-bold tabular-nums', score >= 80 ? 'text-emerald-600' : score >= 60 ? 'text-amber-600' : 'text-rose-600')}>
        {score}
      </span>
      <span className="text-muted-foreground text-sm">/100</span>
      <Badge className={cn('ml-1', colorMap[status] || 'bg-muted')}>{labelMap[status] || status}</Badge>
    </div>
  );
}

function IssueCard({ issue }) {
  const cfg = SEVERITY_CFG[issue.severity] || SEVERITY_CFG.info;
  const { Icon } = cfg;
  return (
    <div className={cn('flex gap-2.5 rounded-lg border px-3 py-2.5 text-[13px]', cfg.cls)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{issue.message}</span>
    </div>
  );
}

function AreaSection({ areaKey, issues, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  const meta = AREA_META[areaKey] || { label: areaKey, Icon: FileText, color: 'text-muted-foreground' };
  const { Icon } = meta;

  const errorCount   = issues.filter(i => i.severity === 'error').length;
  const warningCount = issues.filter(i => i.severity === 'warning').length;

  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <button
        className="flex w-full items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
        onClick={() => setOpen(v => !v)}
      >
        <Icon className={cn('h-4 w-4', meta.color)} />
        <span className="flex-1 text-left font-medium text-[13px]">{meta.label}</span>
        <div className="flex items-center gap-1.5">
          {errorCount > 0 && (
            <Badge variant="destructive" className="h-5 px-1.5 text-[13px]">
              {errorCount} error{errorCount !== 1 ? 's' : ''}
            </Badge>
          )}
          {warningCount > 0 && (
            <Badge className="h-5 px-1.5 text-[13px] bg-amber-100 text-amber-700 border-amber-200">
              {warningCount}
            </Badge>
          )}
          {issues.length === 0 && (
            <Badge className="h-5 px-1.5 text-[13px] bg-emerald-100 text-emerald-700 border-emerald-200">
              <Check className="h-2.5 w-2.5 mr-1" />Pass
            </Badge>
          )}
        </div>
        {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-4 pb-3 space-y-2 border-t border-border bg-muted/10">
          <div className="pt-2" />
          {issues.length === 0 ? (
            <div className="flex items-center gap-2 text-emerald-600 text-[13px]">
              <CheckCircle2 className="h-4 w-4" />
              <span>No issues found in this area.</span>
            </div>
          ) : (
            issues.map((issue) => <IssueCard key={issue.id} issue={issue} />)
          )}
        </div>
      )}
    </div>
  );
}

function groupByArea(issues) {
  const grouped = {};
  for (const key of Object.keys(AREA_META)) {
    grouped[key] = [];
  }
  for (const issue of (issues || [])) {
    const area = issue.area || 'formatting';
    if (!grouped[area]) grouped[area] = [];
    grouped[area].push(issue);
  }
  return grouped;
}

export default function KdpCompliancePanel({ open, onClose, projectId }) {
  const [report, setReport]         = useState(null);     // last completed report
  const [running, setRunning]       = useState(false);
  const [initialLoading, setInitialLoading] = useState(false); // loading last report
  const [jobId,   setJobId]         = useState(null);
  const [aiExpanded, setAiExpanded] = useState(false);
  const pollRef = useRef(null);

  // Load last report on open
  useEffect(() => {
    if (!open || !projectId) return;
    setInitialLoading(true);
    axios.get(`${API}/api/projects/${projectId}/kdp-compliance/last`, { withCredentials: true })
      .then(r => { if (r.data?.has_report) setReport(r.data); })
      .catch(() => {})
      .finally(() => setInitialLoading(false));
  }, [open, projectId]);

  // Poll job while running
  const stopPoll = () => { clearInterval(pollRef.current); pollRef.current = null; };

  const poll = useCallback((jid) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(
          `${API}/api/projects/${projectId}/kdp-compliance/jobs/${jid}`,
          { withCredentials: true },
        );
        const job = r.data;
        if (job.status === 'done') {
          stopPoll();
          setRunning(false);
          setReport(job);
          toast.success('KDP compliance check complete.');
        } else if (job.status === 'failed') {
          stopPoll();
          setRunning(false);
          toast.error(job.error || 'KDP compliance check failed.');
        }
      } catch { stopPoll(); setRunning(false); }
    }, POLL_MS);
  }, [projectId]);

  useEffect(() => { if (!open) stopPoll(); return stopPoll; }, [open]);

  const runCheck = async () => {
    if (!projectId || running) return;
    setRunning(true);
    setReport(null);
    try {
      const r = await axios.post(
        `${API}/api/projects/${projectId}/kdp-compliance/run`,
        {},
        { withCredentials: true },
      );
      setJobId(r.data.job_id);
      poll(r.data.job_id);
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || 'Failed to start compliance check.');
    }
  };

  const handleExport = () => {
    if (!report) return;
    const lines = ['KDP COMPLIANCE REPORT', '='.repeat(40), ''];
    const summary = report.summary || {};
    lines.push(`Overall Score: ${summary.score ?? '-'}/100 — ${summary.status ?? ''}`);
    lines.push(`Errors: ${summary.errors ?? 0} | Warnings: ${summary.warnings ?? 0} | Info: ${summary.infos ?? 0}`);
    lines.push('');
    const grouped = groupByArea(report.issues || []);
    for (const [area, issues] of Object.entries(grouped)) {
      lines.push(`## ${AREA_META[area]?.label || area}`);
      if (issues.length === 0) { lines.push('  ✓ No issues'); }
      else { issues.forEach(i => lines.push(`  [${i.severity.toUpperCase()}] ${i.message}`)); }
      lines.push('');
    }
    if (report.ai_feedback) {
      lines.push('## AI Content Review'); lines.push(report.ai_feedback); lines.push('');
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'kdp-compliance-report.txt';
    a.click(); URL.revokeObjectURL(url);
  };

  const grouped = report ? groupByArea(report.issues || []) : {};
  const summary = report?.summary;
  const totalIssues = (report?.issues || []).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { stopPoll(); onClose(); } }}>
      <DialogContent
        className="max-w-2xl max-h-[88vh] flex flex-col gap-0 p-0 overflow-hidden"
        data-testid="kdp-compliance-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <ClipboardList className="h-4.5 w-4.5 text-primary" />
            KDP Compliance Checker
          </DialogTitle>
          <p className="text-[13.5px] text-muted-foreground mt-0.5">
            Check your manuscript against Amazon KDP requirements before publishing.
          </p>
        </DialogHeader>

        <Separator />

        {/* Top bar */}
        <div className="px-5 py-3 flex items-center gap-3">
          <Button
            data-testid="kdp-run-btn"
            onClick={runCheck}
            disabled={running}
            size="sm"
            className="gap-2"
          >
            {running
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Analysing…</>
              : <><Play className="h-3.5 w-3.5" /> {report ? 'Re-run Check' : 'Run Check'}</>
            }
          </Button>

          {running && (
            <div className="flex items-center gap-2 flex-1">
              <Progress value={undefined} className="h-1.5 flex-1 animate-pulse" />
              <span className="text-[13px] text-muted-foreground whitespace-nowrap">Scanning manuscript…</span>
            </div>
          )}

          {report && !running && (
            <div className="flex items-center gap-3 ml-auto">
              <ScoreBadge score={summary?.score} status={summary?.status} />
              <Button
                data-testid="kdp-export-btn"
                variant="outline" size="sm" className="gap-1.5 h-7 text-[13.5px]"
                onClick={handleExport}
              >
                <Download className="h-3 w-3" /> Export
              </Button>
            </div>
          )}
        </div>

        <Separator />

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-5 py-4 space-y-3">

            {/* Loading last report */}
            {initialLoading && (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <Loader2 className="h-7 w-7 animate-spin text-primary" />
                <p className="text-[13px] text-muted-foreground">Loading last report…</p>
              </div>
            )}

            {/* Analyzing in progress */}
            {running && !initialLoading && (
              <div className="flex flex-col items-center gap-4 py-12 text-center">
                <div className="relative">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <ClipboardList className="h-6 w-6 text-primary" />
                  </div>
                  <Loader2 className="absolute -bottom-1 -right-1 h-4 w-4 animate-spin text-primary" />
                </div>
                <div>
                  <p className="text-[14px] font-semibold">Scanning manuscript…</p>
                  <p className="text-[12px] text-muted-foreground mt-0.5">
                    Checking metadata, content policy, front/back matter, and formatting.
                  </p>
                </div>
              </div>
            )}

            {/* Empty — no report yet */}
            {!report && !running && !initialLoading && (
              <div className="flex flex-col items-center gap-3 py-12 text-center">
                <ClipboardList className="h-10 w-10 text-muted-foreground/40" />
                <p className="text-[13px] text-muted-foreground max-w-xs">
                  Run the compliance check to scan your manuscript for KDP requirements across metadata, content policy, front/back matter, and formatting.
                </p>
              </div>
            )}

            {report && !running && (
              <>
                {/* Summary bar */}
                <div className="rounded-xl border border-border bg-muted/20 px-4 py-3 flex items-center gap-6 flex-wrap">
                  <div>
                    <p className="text-[13px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Chapters</p>
                    <p className="text-[13px] font-semibold">{report.chapter_count ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-[13px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Words</p>
                    <p className="text-[13px] font-semibold">{report.word_count?.toLocaleString() ?? '—'}</p>
                  </div>
                  <div>
                    <p className="text-[13px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">Issues</p>
                    <p className="text-[13px] font-semibold">{totalIssues}</p>
                  </div>
                  {summary?.errors > 0 && (
                    <Badge variant="destructive" className="text-[13px]">
                      {summary.errors} blocking error{summary.errors !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>

                {/* Areas */}
                {Object.entries(grouped).map(([area, issues]) => (
                  <AreaSection
                    key={area}
                    areaKey={area}
                    issues={issues}
                    defaultOpen={issues.some(i => i.severity === 'error') || issues.some(i => i.severity === 'warning')}
                  />
                ))}

                {/* AI feedback */}
                {report.ai_feedback && (
                  <div className="rounded-xl border border-border overflow-hidden">
                    <button
                      className="flex w-full items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
                      onClick={() => setAiExpanded(v => !v)}
                    >
                      <ShieldCheck className="h-4 w-4 text-violet-500" />
                      <span className="flex-1 text-left font-medium text-[13px]">AI Content Policy Review</span>
                      {aiExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                    </button>
                    {aiExpanded && (
                      <div className="px-4 pb-4 pt-2 border-t border-border bg-muted/10">
                        <p className="text-[14px] text-muted-foreground whitespace-pre-wrap leading-relaxed">
                          {report.ai_feedback}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
